import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Fingerprint, AlertCircle, CreditCard, ExternalLink, BadgeCheck, User, Landmark, BriefcaseBusiness } from 'lucide-react';
import './KycVerification.css';

const KYC_STATUS = {
    NOT_STARTED: 'NOT_STARTED',
    INITIATING: 'INITIATING',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED'
};

const KycVerification = ({ userId, onComplete, summary = {} }) => {
    const [status, setStatus] = useState(KYC_STATUS.NOT_STARTED);
    const [error, setError] = useState(null);
    const [requestId, setRequestId] = useState('');
    const [digilockerUrl, setDigilockerUrl] = useState('');
    const [aadhaarInfo, setAadhaarInfo] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL || '';
    const display = useMemo(() => ({
        name: summary.name || 'Applicant',
        kycStatus: summary.kycStatus || '',
        panNumber: summary.panNumber || summary.pan_number || '',
        bankAccountNumber: summary.bankAccountNumber || summary.bank_account_no || '',
        bankIfscCode: summary.bankIfscCode || summary.bank_ifsc || '',
        employer: summary.employer || summary.businessType || summary.designation || 'N/A',
        income: summary.income || '',
        loanAmount: summary.loanAmount || summary.amount || '',
        aadhaar: summary.aadhaar || {},
        manualSalaryStatement: summary.manualSalaryStatement || null
    }), [summary]);

    useEffect(() => {
        const aadhaar = display.aadhaar || {};
        const hasAadhaarData = Boolean(
            aadhaar.uid ||
            aadhaar.aadhaarNumber ||
            aadhaar.maskedAadhaarNumber ||
            aadhaar.name ||
            aadhaar.dob
        );
        const isKycCompleted = ['completed', 'verified', 'under_review'].includes(String(display.kycStatus || '').toLowerCase());
        if (isKycCompleted || hasAadhaarData) {
            setStatus(KYC_STATUS.COMPLETED);
            setAadhaarInfo((prev) => ({
                ...(prev || {}),
                name: aadhaar.name || prev?.name || display.name || '',
                dob: aadhaar.dob || aadhaar.dateOfBirth || prev?.dob || '',
                gender: aadhaar.gender || prev?.gender || '',
                address: aadhaar.address || prev?.address || '',
                uid: aadhaar.uid || aadhaar.aadhaarNumber || aadhaar.maskedAadhaarNumber || prev?.uid || '',
                photoUrl: aadhaar.jpegUrl || aadhaar.photoUrl || prev?.photoUrl || '',
                jpegUrl: aadhaar.jpegUrl || prev?.jpegUrl || '',
                pdfUrl: aadhaar.pdfUrl || prev?.pdfUrl || ''
            }));
        }
    }, [display.aadhaar, display.kycStatus, display.name]);

    const maskAccount = (value) => {
        const digits = String(value || '').replace(/\s+/g, '');
        if (!digits) return 'Not added';
        if (digits.length <= 4) return digits;
        return `${'*'.repeat(Math.max(0, digits.length - 4))}${digits.slice(-4)}`;
    };

    const maskPan = (value) => {
        const pan = String(value || '').trim().toUpperCase();
        if (!pan) return 'Not added';
        return `${pan.slice(0, 2)}***${pan.slice(-3)}`;
    };

    const syncStatus = useCallback(async (rid) => {
        if (!rid) return;
        try {
            const session = JSON.parse(localStorage.getItem('userSession') || '{}');
            const response = await fetch(`${API_URL}/api/digilocker/status`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.token || ''}`
                },
                body: JSON.stringify({ requestId: rid, userId })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                return;
            }

            const record = data.record || {};
            const nextStatus = String(record.status || '').toUpperCase();
            if (nextStatus === 'COMPLETED') {
                setStatus(KYC_STATUS.COMPLETED);
                setAadhaarInfo({
                    requestId: record.request_id || rid,
                    name: record.aadhaar_name || '',
                    dob: record.aadhaar_dob || '',
                    gender: record.aadhaar_gender || '',
                    address: record.aadhaar_address || '',
                    uid: record.aadhaar_uid || '',
                    photoUrl: record.aadhaar_jpeg_url || record.aadhaar_pdf_url || '',
                    pdfUrl: record.aadhaar_pdf_url || '',
                    jpegUrl: record.aadhaar_jpeg_url || '',
                    raw: record.raw_xml_data || null
                });
                setError(null);
                if (onComplete) onComplete(record);
            } else if (nextStatus === 'FAILED') {
                setStatus(KYC_STATUS.FAILED);
                setError('Digilocker verification failed. Please try again.');
            } else {
                setStatus(KYC_STATUS.IN_PROGRESS);
            }
        } catch (err) {
            console.error('Error syncing Digilocker status:', err);
        }
    }, [API_URL, onComplete, userId]);

    // When popup closes → immediately pull eAadhaar data from Signzy
    const fetchEaadhaarAfterConsent = useCallback(async (rid) => {
        if (!rid) return;
        try {
            console.log('🔍 Popup closed, pulling eAadhaar for requestId:', rid);
            setStatus(KYC_STATUS.IN_PROGRESS); // keep spinner while fetching
            const session = JSON.parse(localStorage.getItem('userSession') || '{}');
            const response = await fetch(`${API_URL}/api/digilocker/eaadhaar`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.token || ''}`
                },
                body: JSON.stringify({ requestId: rid, userId })
            });
            const data = await response.json();
            console.log('eAadhaar response:', data);

            if (data.success && data.status === 'completed' && data.kycData) {
                const normalized = data.kycData?.result || data.kycData || {};
                // Also check nested aadharDetail (webhook format)
                const detail = normalized.aadharDetail || normalized.aadhaarDetail || {};
                setStatus(KYC_STATUS.COMPLETED);
                setAadhaarInfo({
                    requestId: rid,
                    name: normalized.name || detail.name || '',
                    dob: normalized.dob || detail.dob || '',
                    gender: normalized.gender || detail.gender || '',
                    address: normalized.address || detail.address || '',
                    uid: normalized.uid || detail.uid || '',
                    photoUrl: normalized.photo || detail.photo || '',
                    jpegUrl: normalized.aadhaarJpeg || normalized.aadhaarJPEG || '',
                    pdfUrl: normalized.aadhaarPdf || normalized.aadhaarPDF || ''
                });
                setError(null);
                if (onComplete) onComplete(data);
            } else {
                // Data not ready yet — user may not have completed consent, or Signzy is slow
                setStatus(KYC_STATUS.FAILED);
                setError(
                    data.status === 'rejected'
                        ? 'Aadhaar data not available yet. Please try again in a moment.'
                        : data.message || 'Verification incomplete. Please try again.'
                );
            }
        } catch (err) {
            console.error('eAadhaar fetch error:', err);
            setStatus(KYC_STATUS.FAILED);
            setError('Could not retrieve Aadhaar data. Please try again.');
        }
    }, [API_URL, onComplete, userId]);

    // Watch for popup closure
    const watchPopup = useCallback((popup, rid) => {
        if (!popup) return;
        const timer = setInterval(() => {
            if (popup.closed) {
                clearInterval(timer);
                fetchEaadhaarAfterConsent(rid);
            }
        }, 800);
        // Safety: stop watching after 10 minutes
        setTimeout(() => clearInterval(timer), 10 * 60 * 1000);
    }, [fetchEaadhaarAfterConsent]);

    const startKyc = async () => {
        setError(null);
        setStatus(KYC_STATUS.INITIATING);

        // Pre-open the window synchronously to bypass popup blockers
        const popup = window.open('about:blank', '_blank');
        if (popup) {
            popup.document.write('<h3>Loading Digilocker securely... Please wait.</h3>');
        }

        try {
            const session = JSON.parse(localStorage.getItem('userSession') || '{}');
            const response = await fetch(`${API_URL}/api/kyc/initiate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.token || ''}`
                },
                body: JSON.stringify({ userId })
            });

            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Failed to initiate Digilocker');
            }

            const rid = data.requestId || data.kid;
            const url = data.url;
            if (!rid || !url) {
                throw new Error('Digilocker did not return a request URL');
            }

            setRequestId(rid);
            setDigilockerUrl(url);
            setStatus(KYC_STATUS.IN_PROGRESS);

            if (popup) {
                popup.location.href = url;
                // Watch for popup close → pull eAadhaar immediately
                watchPopup(popup, rid);
            } else {
                // Popup was blocked — fall back: user can click "Try Again" after consenting
                setError('Popup was blocked. Please allow popups and try again.');
                setStatus(KYC_STATUS.FAILED);
            }
        } catch (err) {
            console.error('KYC initiation error:', err);
            setError(err.message || 'Something went wrong. Please try again.');
            setStatus(KYC_STATUS.FAILED);
            if (popup) {
                popup.close();
            }
        }
    };

    const renderStatusPill = () => {
        if (status === KYC_STATUS.COMPLETED) return <span className="kyc-status-badge status-completed">✓ Completed</span>;
        if (status === KYC_STATUS.INITIATING || status === KYC_STATUS.IN_PROGRESS) return <span className="kyc-status-badge status-in_progress">🔄 In Progress</span>;
        if (status === KYC_STATUS.FAILED) return <span className="kyc-status-badge status-failed">Failed</span>;
        return <span className="kyc-status-badge status-not_started">Pending</span>;
    };

    const renderAction = () => {
        if (status === KYC_STATUS.COMPLETED) {
            return <button className="kyc-button" onClick={startKyc}><BadgeCheck size={18} />Refresh Digilocker</button>;
        }
        if (status === KYC_STATUS.INITIATING || status === KYC_STATUS.IN_PROGRESS) {
            return (
                <div className="kyc-inline-actions">
                    <button className="kyc-button" onClick={startKyc}>
                        <CreditCard size={18} />
                        Open Digilocker again
                    </button>
                    {digilockerUrl && (
                        <a href={digilockerUrl} target="_blank" rel="noreferrer" className="kyc-link">
                            Open link again <ExternalLink size={14} />
                        </a>
                    )}
                </div>
            );
        }
        if (status === KYC_STATUS.FAILED) {
            return <button className="kyc-button" onClick={startKyc}><AlertCircle size={18} />Try Again</button>;
        }
        return <button className="kyc-button" onClick={startKyc}><CreditCard size={18} />Start Digilocker</button>;
    };

    const isAadhaarCompleted = status === KYC_STATUS.COMPLETED;
    const cards = [
        {
            icon: <Fingerprint size={24} />,
            title: 'Aadhaar',
            value: isAadhaarCompleted ? 'Verification complete' : (aadhaarInfo?.name || display.name || 'Fetch with Digilocker'),
            meta: isAadhaarCompleted ? 'Digilocker verification successful' : (aadhaarInfo?.uid ? `UID: ${aadhaarInfo.uid}` : 'Consent-based fetch'),
            statusLabel: isAadhaarCompleted ? 'Completed' : 'Pending',
            accent: 'aadhaar',
            action: !isAadhaarCompleted
        },
        {
            icon: <User size={24} />,
            title: 'PAN',
            value: maskPan(display.panNumber),
            meta: display.name || 'Borrower identity',
            statusLabel: display.panNumber ? 'Captured' : 'Pending',
            accent: 'pan'
        },
        {
            icon: <Landmark size={24} />,
            title: 'Bank Account',
            value: maskAccount(display.bankAccountNumber),
            meta: display.bankIfscCode ? `IFSC: ${display.bankIfscCode}` : 'Awaiting penny drop',
            statusLabel: display.bankAccountNumber ? 'Verified' : 'Pending',
            accent: 'bank'
        },
        {
            icon: <BriefcaseBusiness size={24} />,
            title: 'Salary / Income',
            value: display.income ? `₹${Number(display.income).toLocaleString('en-IN')}` : display.employer,
            meta: (() => {
                const match = display.manualSalaryStatement?.match;
                if (match?.checked) {
                    return match.allMatched
                        ? 'Manual statement match found (company, designation, salary)'
                        : `Manual statement partial match • Company: ${match.companyMatched ? 'Yes' : 'No'} • Designation: ${match.designationMatched ? 'Yes' : 'No'} • Salary: ${match.salaryMatched ? 'Yes' : 'No'}`;
                }
                return display.employer || 'Employment details';
            })(),
            statusLabel: display.income ? 'Captured' : 'Pending',
            accent: 'salary'
        }
    ];

    return (
        <div className="kyc-card kyc-card-wide">
            <div className="kyc-grid-header">
                <div>
                    <div className="kyc-kicker">Verification Checklist</div>
                    <h3 className="kyc-title">Identity, bank, and income checks</h3>
                    <p className="kyc-description">
                        Review the verification cards below and use Digilocker when you are ready to fetch Aadhaar details.
                    </p>
                </div>
                {renderStatusPill()}
            </div>

            <div className="kyc-check-grid">
                {cards.map(card => (
                    <div key={card.title} className={`kyc-check-card kyc-${card.accent}`}>
                        <div className="kyc-check-top">
                            <div className="kyc-check-icon">{card.icon}</div>
                            {card.statusLabel && <span className="kyc-mini-badge">{card.statusLabel}</span>}
                        </div>
                        <h4>{card.title}</h4>
                        <div className="kyc-check-value">{card.value}</div>
                        <div className="kyc-check-meta">{card.meta}</div>
                        {card.action && (
                            <div style={{ marginTop: '1rem' }}>
                                {renderAction()}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {error && (
                <div className="kyc-error-message" style={{ marginTop: '1rem' }}>
                    {error}
                </div>
            )}

            {status === KYC_STATUS.INITIATING || status === KYC_STATUS.IN_PROGRESS ? (
                <div style={{ marginTop: '1rem' }}>
                    {digilockerUrl && (
                        <a href={digilockerUrl} target="_blank" rel="noreferrer" className="kyc-link">
                            Open Digilocker link again <ExternalLink size={14} />
                        </a>
                    )}
                </div>
            ) : null}

            <style>{`
                .animate-spin {
                    animation: kyc-spin 1s linear infinite;
                }
                .kyc-card-wide {
                    max-width: 980px;
                    text-align: left;
                }
                .kyc-grid-header {
                    display: flex;
                    justify-content: space-between;
                    gap: 1rem;
                    align-items: flex-start;
                    margin-bottom: 0.85rem;
                }
                .kyc-kicker {
                    text-transform: uppercase;
                    letter-spacing: 0.18em;
                    font-size: 0.68rem;
                    font-weight: 800;
                    color: #a5761e;
                    margin-bottom: 0.35rem;
                }
                .kyc-check-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
                    gap: 0.7rem;
                }
                .kyc-check-card {
                    border: 1px solid #e5e7eb;
                    border-radius: 14px;
                    padding: 0.75rem;
                    background: linear-gradient(180deg, #fff 0%, #fafafa 100%);
                    min-height: 128px;
                    display: flex;
                    flex-direction: column;
                }
                .kyc-check-top {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 0.55rem;
                }
                .kyc-check-icon {
                    width: 34px;
                    height: 34px;
                    border-radius: 10px;
                    display: grid;
                    place-items: center;
                    background: #f3e9d2;
                    color: #a5761e;
                }
                .kyc-mini-badge {
                    font-size: 0.62rem;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 0.08em;
                    color: #475569;
                    background: #f1f5f9;
                    border-radius: 999px;
                    padding: 0.24rem 0.55rem;
                }
                .kyc-check-card h4 {
                    margin: 0 0 0.22rem;
                    font-size: 0.86rem;
                }
                .kyc-check-value {
                    font-size: 0.88rem;
                    font-weight: 800;
                    color: #0f172a;
                    margin-bottom: 0.12rem;
                    line-height: 1.25;
                }
                .kyc-check-meta {
                    color: #64748b;
                    font-size: 0.74rem;
                    line-height: 1.3;
                }
                .kyc-inline-actions {
                    display: flex;
                    flex-direction: column;
                    gap: 0.45rem;
                    margin-top: auto;
                }
                .kyc-summary {
                    margin: 0 auto 1rem;
                    max-width: 320px;
                    display: grid;
                    gap: 0.5rem;
                    text-align: left;
                    padding: 1rem;
                    border-radius: 14px;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    color: #334155;
                }
                .kyc-summary-wide {
                    max-width: 100%;
                    margin-top: 1rem;
                    grid-template-columns: repeat(2, minmax(0, 1fr));
                }
                .kyc-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.35rem;
                    color: #2563eb;
                    font-weight: 600;
                    text-decoration: none;
                    margin-bottom: 1rem;
                }
                .kyc-link:hover {
                    text-decoration: underline;
                }
                @media (max-width: 720px) {
                    .kyc-grid-header {
                        flex-direction: column;
                    }
                    .kyc-check-grid {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default KycVerification;
